import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, Stack, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  RadioGroup, FormControlLabel, Radio, Alert, CircularProgress, Tooltip,
  LinearProgress, Breadcrumbs, Link as MuiLink, Snackbar,
  Accordion, AccordionSummary, AccordionDetails, Stepper, Step, StepLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Eject as EjectIcon,
  PowerSettingsNew as MountIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Build as FormatIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckIcon,
  WarningAmber as WarnIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import PhysicalDeliveryMarkDialog from '../../components/PhysicalDeliveryMarkDialog';
import {
  DRIVE_STAGES, stageIndex, kdmState, physicalStatus, nextStep, isActiveDelivery,
} from '../../utils/physicalDelivery';

function bytesHuman(n) {
  if (!n && n !== 0) return 'unknown';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function timeAgo(epochSec) {
  if (!epochSec) return '';
  const diff = Math.floor(Date.now() / 1000 - epochSec);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function PhysicalDrives() {
  const [nodes, setNodes] = useState([]);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [nodeStatus, setNodeStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const [formatFs, setFormatFs] = useState('ext4');
  // v10.20.7: each format gets a unique label so drives plugged into
  // the same Pi at different times can be told apart. The backend
  // also re-generates if we send blank — this client-side default
  // just shows the operator what they're about to apply (and lets
  // them edit if they want a route-specific name like BEVERLY-001).
  // Token alphabet excludes O/0/I/1 to match the backend so the
  // operator's tape label can't be misread.
  const _genDriveLabel = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let token = '';
    for (let i = 0; i < 6; i++) {
      token += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return `HHMG-${token}`;
  };
  const [formatLabel, setFormatLabel] = useState(_genDriveLabel());
  const [confirmFormat, setConfirmFormat] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snack, setSnack] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  // v12.0.0: always fetch history; the toggle only controls rendering.
  const [showHistory, setShowHistory] = useState(false);
  // Set by a successful Eject so the slot card can say "safe to unplug"
  // even after the helper powers the USB port down and the disk vanishes.
  const [ejectedAt, setEjectedAt] = useState(null);
  const [statusCheckedAt, setStatusCheckedAt] = useState(null);
  // Receipt + test tracking dialog state.
  const [trackTarget, setTrackTarget] = useState(null);  // delivery row
  const [trackKind, setTrackKind] = useState(null);      // 'received' | 'tested'
  // v10.18.20: trackName/trackNotes state was inlined into the
  // dialog; the shared PhysicalDeliveryMarkDialog now owns it.
  // Ship dialog state (v10.17.30): carrier + tracking number captured at
  // the moment the operator hands the drive to the courier.
  const [shipTarget, setShipTarget] = useState(null);    // delivery row
  const [shipCarrier, setShipCarrier] = useState('');
  const [shipTracking, setShipTracking] = useState('');
  // FedEx schedule dialog state (v10.17.34).
  const [fedexTarget, setFedexTarget] = useState(null);  // delivery row
  const [fedexService, setFedexService] = useState('FEDEX_GROUND');
  const [fedexWeight, setFedexWeight] = useState(5);
  const [fedexBusy, setFedexBusy] = useState(false);

  const node = useMemo(() => nodes.find((n) => n.id === activeNodeId) || null, [nodes, activeNodeId]);

  const fetchNodes = useCallback(async () => {
    try {
      const { data } = await api.get('/api/physical-delivery/nodes');
      setNodes(data || []);
      if ((data || []).length && !activeNodeId) {
        setActiveNodeId(data[0].id);
      }
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
    }
  }, [activeNodeId]);

  const refreshStatus = useCallback(async (nodeId) => {
    if (!nodeId) return;
    setLoading(true);
    setStatusError(null);
    try {
      const { data } = await api.get(`/api/physical-delivery/nodes/${nodeId}/status`);
      setNodeStatus(data);
      setStatusCheckedAt(Date.now());
    } catch (e) {
      setStatusError(e.response?.data?.detail || e.message);
      setNodeStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFiles = useCallback(async (nodeId, p) => {
    if (!nodeId) return;
    try {
      const { data } = await api.get(`/api/physical-delivery/nodes/${nodeId}/files`, { params: { path: p || '' } });
      setFiles(data.entries || []);
    } catch (_e) {
      setFiles([]);
      // not necessarily an error — drive might just not be mounted
    }
  }, []);

  useEffect(() => { fetchNodes(); }, [fetchNodes]);
  useEffect(() => {
    if (activeNodeId) {
      refreshStatus(activeNodeId);
      setPath('');
    }
  }, [activeNodeId, refreshStatus]);
  useEffect(() => {
    if (activeNodeId && nodeStatus?.mounted) {
      fetchFiles(activeNodeId, path);
    } else {
      setFiles([]);
    }
  }, [activeNodeId, path, nodeStatus?.mounted, fetchFiles]);

  // Deliveries panel. Polls every 5s while any row is staging so the
  // progress bar moves on its own; idles to no-op once everything
  // terminal-ish. Operator can click "Show completed" to see history.
  const fetchDeliveries = useCallback(async () => {
    try {
      const { data } = await api.get('/api/physical-delivery/deliveries', {
        params: { include_terminal: true },
      });
      setDeliveries(data || []);
    } catch (_e) { /* silent, non-fatal */ }
  }, []);

  useEffect(() => { fetchDeliveries(); }, [fetchDeliveries]);

  // v10.17.27: ALWAYS poll. Pre-fix the polling only ran while at
  // least one row was in staging/pending — once everything went
  // terminal the interval stopped, and a NEW row going back to
  // staging (e.g. backend-side re-stage, recovery flip, operator
  // resets row via SQL) was never observed because nothing woke the
  // page up. The 30s idle cadence is cheap (one GET) and means the
  // panel reflects any DB change within at most 30s without a hard
  // refresh; the 5s active cadence keeps progress bars moving in
  // real time during a transfer.
  useEffect(() => {
    const anyActive = (deliveries || []).some(d =>
      d.status === 'staging' || d.status === 'pending'
    );
    const intervalMs = anyActive ? 5000 : 30000;
    const id = setInterval(fetchDeliveries, intervalMs);
    return () => clearInterval(id);
  }, [deliveries, fetchDeliveries]);

  const runOp = async (op, body, successMsg) => {
    if (!activeNodeId) return;
    setBusy(true);
    try {
      const { data } = body
        ? await api.post(`/api/physical-delivery/nodes/${activeNodeId}/${op}`, body)
        : await api.post(`/api/physical-delivery/nodes/${activeNodeId}/${op}`);
      setNodeStatus(data);
      setStatusCheckedAt(Date.now());
      setEjectedAt(op === 'eject' ? Date.now() : null);
      setSnack({ severity: 'success', msg: successMsg });
      if (op === 'format' || op === 'unmount' || op === 'eject') {
        setPath('');
        setFiles([]);
      }
      if (op === 'mount') {
        fetchFiles(activeNodeId, '');
      }
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleFormat = async () => {
    if (confirmFormat !== 'FORMAT') return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/physical-delivery/nodes/${activeNodeId}/format`, {
        filesystem: formatFs,
        label: formatLabel,
        confirm: true,
      });
      setNodeStatus(data);
      setFormatOpen(false);
      setConfirmFormat('');
      // v10.17.21: explicit post-format refresh. The format endpoint
      // returns the helper's view of the drive AS OF when the
      // helper completed, but the SSH-based comp01 node sometimes
      // settles its mount state a beat later. Re-pull /status to
      // catch the post-mount truth, and reset the files panel +
      // deliveries list so the page reflects "fresh formatted drive"
      // end to end instead of leaving stale rows from the previous
      // drive on screen.
      setPath('');
      setFiles([]);
      refreshStatus(activeNodeId);
      fetchDeliveries();
      // Backend echoes the label it actually applied (its own
       // generator runs when we send blank). Surface so the operator
       // knows what to write on tape.
       const applied = data?.applied_label || formatLabel;
       setSnack({
         severity: 'success',
         msg: `Drive formatted ${formatFs}. Label "${applied}": write this on the drive.`,
       });
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/api/physical-delivery/nodes/${activeNodeId}/files`, { params: { path: deleteTarget.rel_path } });
      setDeleteTarget(null);
      fetchFiles(activeNodeId, path);
      refreshStatus(activeNodeId);
      setSnack({ severity: 'success', msg: `deleted ${deleteTarget.name}` });
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const assignDelivery = async (deliveryId, { stack = false } = {}) => {
    if (!activeNodeId) {
      setSnack({ severity: 'warning', msg: 'Pick a node first.' });
      return;
    }
    if (!nodeStatus?.mounted) {
      setSnack({ severity: 'warning', msg: 'The drive on the selected node is not mounted. Mount it first.' });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/physical-delivery/deliveries/${deliveryId}/assign`, {
        node_id: activeNodeId,
        stack,
      });
      setSnack({ severity: 'success',
                 msg: stack
                   ? `Delivery #${deliveryId} added to the current drive. Copy started.`
                   : `Delivery #${deliveryId} assigned. Copy started.` });
      fetchDeliveries();
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  // v10.17.30: shipping opens a dialog that captures carrier + tracking
  // number, then sends ONE PATCH (status + carrier + tracking).
  const openShip = (d) => {
    setShipTarget(d);
    setShipCarrier(d.carrier || '');
    setShipTracking(d.tracking_number || '');
  };

  const openFedex = (d) => {
    setFedexTarget(d);
    setFedexService('FEDEX_GROUND');
    setFedexWeight(5);
  };

  // Operator panel records the carrier POD; received / tested live on
  // the DCP Distribution view (v10.18.20).
  const openDelivered = (d) => {
    setTrackTarget(d);
    setTrackKind('delivered');
  };

  // v10.18.8: fetch the manifest through axios (bearer token lives in
  // localStorage, not cookies, so a plain <a href> would 401), then open
  // the blob in a new tab.
  const printManifest = async (d) => {
    try {
      const resp = await api.get(
        `/api/physical-delivery/deliveries/${d.id}/manifest.pdf`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // noopener so the popup can't navigate us.
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoking too early kills the source mid-render.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setSnack({
        severity: 'error',
        msg: e.response?.data?.detail || e.message,
      });
    }
  };

  const refreshTracking = async (d) => {
    try {
      const { data } = await api.post(`/api/fedex/track/${d.tracking_number}`);
      if (data.status === 'delivered') {
        setSnack({ severity: 'success',
          msg: `Delivered ${data.delivered_at || ''}${data.signed_for_by_name ? ', signed by ' + data.signed_for_by_name : ''}` });
      } else {
        setSnack({ severity: 'info',
          msg: `${data.status}: ${data.latest_event || 'no events yet'}` });
      }
      await fetchDeliveries();
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
    }
  };

  const copyTracking = (d) => {
    navigator.clipboard?.writeText(d.tracking_number);
    setSnack({ severity: 'success', msg: 'Tracking number copied' });
  };

  const breadcrumbParts = path ? path.split('/').filter(Boolean) : [];

  // v12.0.0: the page is organised around getting a drive out the door,
  // not around the Pi's hardware. Active deliveries are cards with ONE
  // highlighted next step; finished ones drop into a History table.
  const activeDeliveries = deliveries.filter(isActiveDelivery);
  const historyDeliveries = deliveries.filter((d) => !isActiveDelivery(d));
  // Drive-busy: another delivery on the same node is still in the slot
  // (files written, not yet shipped). v10.20.4: SHIPPED / DELIVERED /
  // RETURNED no longer count; once the drive leaves the Pi the slot is
  // free. A STAGED row can still go stale if the operator unplugged the
  // drive without marking it shipped; we can't tell that from DB state.
  const driveBusyDelivery = deliveries.find(d =>
    d.node_id === activeNodeId &&
    ['staging', 'staged'].includes(d.status)
  );
  // Mount state of the slot a delivery's drive sits in. null = unknown
  // (another node selected, or status not loaded); nextStep() treats
  // unknown as "eject first" so shipping is never offered for a drive
  // that might still be mounted.
  const driveMountedFor = (d) => (
    d.node_id === activeNodeId && nodeStatus ? Boolean(nodeStatus.mounted) : null
  );

  const cardProps = {
    busy, node, nodeStatus, driveBusyDelivery,
    driveMountedFor, assignDelivery,
    onEject: () => runOp('eject', null, 'Drive ejected. Safe to unplug.'),
    openShip, openFedex, openDelivered, printManifest, refreshTracking, copyTracking,
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Physical Drives</Typography>
          <Typography variant="body2" color="text.secondary">
            Copy DCPs onto USB drives and track each drive to the venue.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} size="small" onClick={() => {
          fetchNodes(); fetchDeliveries(); if (activeNodeId) refreshStatus(activeNodeId);
        }}>
          Refresh
        </Button>
      </Stack>

      {nodes.length === 0 && (
        <Alert severity="info">
          No physical delivery nodes registered. (Seed one via the API or directly in <code>physical_delivery_nodes</code>.)
        </Alert>
      )}

      {nodes.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {nodes.map((n) => (
            <Chip
              key={n.id}
              label={n.name}
              color={n.id === activeNodeId ? 'primary' : 'default'}
              onClick={() => setActiveNodeId(n.id)}
              variant={n.id === activeNodeId ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      )}

      {node && (
        <DriveSlotCard
          node={node}
          nodeStatus={nodeStatus}
          statusError={statusError}
          statusCheckedAt={statusCheckedAt}
          loading={loading}
          busy={busy}
          ejectedAt={ejectedAt}
          onMount={() => runOp('mount', null, 'Drive mounted')}
          onEject={() => runOp('eject', null, 'Drive ejected. Safe to unplug.')}
          onFormat={() => { setFormatLabel(_genDriveLabel()); setFormatOpen(true); }}
        />
      )}

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Deliveries in progress
            {activeDeliveries.length > 0 && (
              <Chip size="small" label={activeDeliveries.length}
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
            )}
          </Typography>
        </Stack>
        {driveBusyDelivery && activeDeliveries.some(d => ['requested', 'pending'].includes(d.status)) && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
            The drive on <code>{node?.name}</code> already holds delivery <strong>#{driveBusyDelivery.id}</strong>
            {driveBusyDelivery.venue_name ? ` (${driveBusyDelivery.venue_name})` : ''}.
            Use <strong>Add to drive</strong> to put another delivery on the same drive (for multi-venue routes),
            or ship that drive and plug in a fresh one.
          </Alert>
        )}
        {activeDeliveries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing in progress. Request a drive from Theatrical DCP, Distribution.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {activeDeliveries.map((d) => <DeliveryCard key={d.id} d={d} {...cardProps} />)}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            History
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {historyDeliveries.length} {historyDeliveries.length === 1 ? 'delivery' : 'deliveries'}
            </Typography>
          </Typography>
          <Button size="small" onClick={() => setShowHistory((v) => !v)}
                  endIcon={<ExpandMoreIcon sx={{ transform: showHistory ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}>
            {showHistory ? 'Hide history' : 'Show history'}
          </Button>
        </Stack>
        {showHistory && (
          historyDeliveries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No finished deliveries yet.</Typography>
          ) : (
            <HistoryTable rows={historyDeliveries} {...cardProps} />
          )
        )}
      </Paper>

      {node && nodeStatus?.mounted && (
        <Accordion variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Browse drive contents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
                <MuiLink component="button" underline="hover" onClick={() => setPath('')}
                         sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HomeIcon fontSize="small" /> {nodeStatus.label || node.mountpoint}
                </MuiLink>
                {breadcrumbParts.map((part, i) => {
                  const upto = breadcrumbParts.slice(0, i + 1).join('/');
                  const isLast = i === breadcrumbParts.length - 1;
                  return isLast
                    ? <Typography key={i} variant="body2">{part}</Typography>
                    : <MuiLink key={i} component="button" underline="hover" onClick={() => setPath(upto)}>{part}</MuiLink>;
                })}
              </Breadcrumbs>
              <Typography variant="caption" color="text.secondary">
                {files.length} {files.length === 1 ? 'item' : 'items'}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Modified</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.length === 0 && (
                    <TableRow><TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Empty.
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {files.map((f) => (
                    <TableRow key={f.rel_path} hover sx={{ cursor: f.is_dir ? 'pointer' : 'default' }}>
                      <TableCell onClick={() => f.is_dir && setPath(f.rel_path)}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {f.is_dir ? <FolderIcon fontSize="small" color="primary" /> : <FileIcon fontSize="small" />}
                          <span>{f.name}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{f.is_dir ? '' : bytesHuman(f.size_bytes)}</TableCell>
                      <TableCell><code style={{ fontSize: '0.75rem' }}>{f.mode}</code></TableCell>
                      <TableCell>{timeAgo(f.mtime)}</TableCell>
                      <TableCell>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(f); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Format dialog */}
      <Dialog open={formatOpen} onClose={() => !busy && setFormatOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Format drive</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This permanently erases everything on the drive. Eject the drive first if it's currently mounted.
          </Alert>
          {nodeStatus?.disk && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Target: <code>{nodeStatus.partition || nodeStatus.disk}</code> ({bytesHuman(nodeStatus.size_bytes)})
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Filesystem</Typography>
          <RadioGroup value={formatFs} onChange={(e) => setFormatFs(e.target.value)}>
            <FormControlLabel value="ext4" control={<Radio />} label="ext4 (modern, recommended for newer media servers)" />
            <FormControlLabel value="ext3" control={<Radio />} label="ext3 (broad compatibility, journaled)" />
            <FormControlLabel value="ext2" control={<Radio />} label="ext2 (maximum compatibility with older DCI servers)" />
          </RadioGroup>
          <TextField fullWidth margin="dense" label="Volume label" value={formatLabel}
                     onChange={(e) => setFormatLabel(e.target.value.slice(0, 16))} helperText="Max 16 chars" />
          <TextField fullWidth margin="dense" label='Type "FORMAT" to confirm' value={confirmFormat}
                     onChange={(e) => setConfirmFormat(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormatOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={handleFormat} disabled={busy || confirmFormat !== 'FORMAT'} variant="contained" color="warning">
            {busy ? <CircularProgress size={18} /> : 'Format'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delivered (carrier POD) — venue-side received/tested marks
          live on the DCP Distribution view per v10.18.20. The shared
          PhysicalDeliveryMarkDialog handles all three lifecycle
          kinds, but the operator panel only opens the 'delivered'
          flow. trackTarget+trackKind state stays for the existing
          openers; the dialog component lives in components/. */}
      <PhysicalDeliveryMarkDialog
        open={Boolean(trackTarget)}
        onClose={() => setTrackTarget(null)}
        onSaved={(kind, id) => {
          setSnack({ severity: 'success', msg: `Delivery #${id} marked ${kind}` });
          fetchDeliveries();
        }}
        kind={trackKind}
        delivery={trackTarget}
      />

      {/* v10.17.30: Ship dialog — carrier + tracking number captured at
          the moment the operator hands the drive to the courier. The
          tracking number is what the venue + production company will
          look at to know when the drive is en route. */}
      <Dialog open={Boolean(shipTarget)} onClose={() => !busy && setShipTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {shipTarget?.tracking_number ? 'Update tracking' : 'Mark shipped'}
        </DialogTitle>
        <DialogContent>
          {shipTarget && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Delivery #{shipTarget.id} · {shipTarget.venue_name || 'venue'} ·{' '}
              {shipTarget.ship_to_name || 'no ship-to name'}
            </Typography>
          )}
          <TextField fullWidth margin="dense" size="small" select
                     SelectProps={{ native: true }}
                     label="Carrier"
                     value={shipCarrier} onChange={(e) => setShipCarrier(e.target.value)}>
            <option value="">(none)</option>
            <option value="FedEx">FedEx</option>
            <option value="UPS">UPS</option>
            <option value="USPS">USPS</option>
            <option value="DHL">DHL</option>
            <option value="Hand-delivered">Hand-delivered</option>
            <option value="Courier">Courier</option>
          </TextField>
          <TextField fullWidth margin="dense" size="small" autoFocus
                     label="Tracking number"
                     value={shipTracking} onChange={(e) => setShipTracking(e.target.value)}
                     placeholder="e.g. 1Z999AA10123456784" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Visible on the Distribution page so the venue and production team
            can track the drive in transit.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipTarget(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={busy || !shipTarget}
                  onClick={async () => {
                    if (!shipTarget) return;
                    setBusy(true);
                    try {
                      const payload = {
                        carrier: shipCarrier || null,
                        tracking_number: shipTracking || null,
                      };
                      // Only transition status if we're still on STAGED.
                      // For shipped/delivered rows this is an in-place
                      // tracking-number edit.
                      if (shipTarget.status === 'staged') {
                        payload.status = 'shipped';
                      }
                      await api.patch(`/api/physical-delivery/deliveries/${shipTarget.id}`, payload);
                      setSnack({ severity: 'success',
                                 msg: shipTarget.status === 'staged'
                                   ? `Delivery #${shipTarget.id} marked shipped`
                                   : `Delivery #${shipTarget.id} tracking updated` });
                      setShipTarget(null);
                      fetchDeliveries();
                    } catch (e) {
                      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
                    } finally {
                      setBusy(false);
                    }
                  }}>
            {busy ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* v10.17.34: Schedule FedEx pickup. Creates a shipment via the
          FedEx Ship API, returns tracking number + label URL, flips
          the row to SHIPPED. The recipient address is resolved
          server-side from venue.shipping_address (operator can wire
          an override later if a venue needs ad-hoc routing). */}
      <Dialog open={Boolean(fedexTarget)} onClose={() => !fedexBusy && setFedexTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule FedEx pickup</DialogTitle>
        <DialogContent>
          {fedexTarget && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Delivery #{fedexTarget.id} · {fedexTarget.venue_name || 'venue'} ·{' '}
              {fedexTarget.ship_to_name || 'no ship-to name'}
            </Typography>
          )}
          <TextField fullWidth margin="dense" size="small" select
                     SelectProps={{ native: true }}
                     label="Service"
                     value={fedexService} onChange={(e) => setFedexService(e.target.value)}>
            <option value="FEDEX_GROUND">FedEx Ground</option>
            <option value="FEDEX_EXPRESS_SAVER">FedEx Express Saver (3-day)</option>
            <option value="FEDEX_2_DAY">FedEx 2Day</option>
            <option value="STANDARD_OVERNIGHT">Standard Overnight</option>
            <option value="PRIORITY_OVERNIGHT">Priority Overnight</option>
          </TextField>
          <TextField fullWidth margin="dense" size="small" type="number" inputProps={{ step: 0.1, min: 0.1 }}
                     label="Weight (lbs)"
                     value={fedexWeight} onChange={(e) => setFedexWeight(parseFloat(e.target.value) || 0)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Default package dims (9×6×2 in) match a 2.5" USB drive in a
            padded mailer. Override later via the API if shipping a
            larger case.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFedexTarget(null)} disabled={fedexBusy}>Cancel</Button>
          <Button variant="contained" disabled={fedexBusy || !fedexTarget}
                  onClick={async () => {
                    if (!fedexTarget) return;
                    setFedexBusy(true);
                    try {
                      const { data } = await api.post(
                        `/api/physical-delivery/deliveries/${fedexTarget.id}/fedex/ship`,
                        { service_type: fedexService, weight_lbs: fedexWeight },
                      );
                      setSnack({
                        severity: 'success',
                        msg: `Shipment created. Tracking ${data.tracking_number}`,
                      });
                      setFedexTarget(null);
                      fetchDeliveries();
                    } catch (e) {
                      setSnack({ severity: 'error', msg: e.response?.data?.detail || e.message });
                    } finally { setFedexBusy(false); }
                  }}>
            {fedexBusy ? <CircularProgress size={18} /> : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !busy && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete from drive?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <code>{deleteTarget?.rel_path}</code> ({deleteTarget?.is_dir ? 'directory + contents' : bytesHuman(deleteTarget?.size_bytes)})
          </Typography>
          <Typography variant="caption" color="text.secondary">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>Cancel</Button>
          <Button onClick={handleDelete} disabled={busy} color="error" variant="contained">
            {busy ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnack(null)}
      >
        {snack ? (
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)} sx={{ maxWidth: 480 }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// Manifest is a packing slip for a drive that has content on it.
function canPrintManifest(d) {
  return !['requested', 'pending', 'staging'].includes(d.status);
}

function deliveryTitle(d) {
  return d.dcp_title || (d.dcp_job_id ? `DCP #${d.dcp_job_id}` : `Delivery #${d.id}`);
}

function DriveSlotCard({
  node, nodeStatus, statusError, statusCheckedAt, loading, busy, ejectedAt,
  onMount, onEject, onFormat,
}) {
  const hasDrive = Boolean(nodeStatus?.disk);
  const mounted = Boolean(nodeStatus?.mounted);
  const used = nodeStatus?.used_bytes || 0;
  const size = nodeStatus?.size_bytes || 0;
  const usedPct = size ? Math.min(100, (used / size) * 100) : 0;

  let headline;
  let sub = null;
  let chip = null;
  if (statusError) {
    headline = `Can't reach ${node.name}`;
    sub = statusError;
    chip = { label: 'Unreachable', color: 'error' };
  } else if (!nodeStatus) {
    headline = 'Checking the drive slot';
  } else if (mounted) {
    headline = nodeStatus.label || 'Unlabeled drive';
    chip = { label: 'Mounted', color: 'success' };
    sub = `${bytesHuman(used)} used of ${bytesHuman(size)}, ${bytesHuman(nodeStatus.free_bytes)} free`;
  } else if (hasDrive) {
    headline = nodeStatus.label || 'Unlabeled drive';
    chip = ejectedAt ? { label: 'Ejected, safe to unplug', color: 'info' } : { label: 'Not mounted', color: 'default' };
    sub = ejectedAt
      ? 'Unplug the drive, then mark it shipped below.'
      : 'Mount the drive to copy onto it, or format a new one.';
  } else {
    headline = ejectedAt ? 'Drive ejected' : 'No drive in slot';
    chip = ejectedAt ? { label: 'Safe to unplug', color: 'info' } : null;
    sub = ejectedAt ? 'Unplug the drive, then mark it shipped below.' : `Plug a USB drive into ${node.name}.`;
  }

  const details = [
    `Node: ${node.name} (${node.ssh_user}@${node.hostname}:${node.ssh_port})`,
    `Mountpoint: ${node.mountpoint}`,
    nodeStatus?.disk && `Device: ${nodeStatus.disk}`,
    nodeStatus?.partition && `Partition: ${nodeStatus.partition}`,
    nodeStatus?.fs && `Filesystem: ${nodeStatus.fs}`,
    nodeStatus?.uuid && `UUID: ${nodeStatus.uuid}`,
    statusCheckedAt && `Checked ${new Date(statusCheckedAt).toLocaleTimeString()}`,
  ].filter(Boolean);

  return (
    <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
             alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Drive slot on {node.name}
            </Typography>
            <Tooltip arrow title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{details.join('\n')}</Box>}>
              <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontFamily: mounted || hasDrive ? 'monospace' : undefined }}>
              {headline}
            </Typography>
            {chip && <Chip size="small" label={chip.label} color={chip.color} />}
          </Stack>
          {mounted && nodeStatus?.label && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Write this label on the drive.
            </Typography>
          )}
          {sub && (
            <Typography variant="body2" color={statusError ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5 }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {mounted ? (
            // v10.17.33: Eject only (unmount + USB power-off). A bare
            // Unmount leaves the drive electrically attached, which can
            // confuse /dev assignment for the next drive.
            <Button size="small" variant="contained" startIcon={<EjectIcon />} disabled={busy}
                    onClick={onEject}>
              Eject
            </Button>
          ) : (
            <Button size="small" variant="outlined" startIcon={<MountIcon />} disabled={busy || !hasDrive}
                    onClick={onMount}>
              Mount
            </Button>
          )}
          <Button size="small" variant="outlined" color="warning" startIcon={<FormatIcon />}
                  disabled={busy || mounted} onClick={onFormat}>
            Format
          </Button>
        </Stack>
      </Stack>
      {mounted && size > 0 && (
        <LinearProgress variant="determinate" value={usedPct} sx={{ mt: 1.5, height: 6, borderRadius: 3 }} />
      )}
      {loading && <LinearProgress sx={{ mt: 1.5 }} />}
    </Paper>
  );
}

function KdmChip({ d }) {
  const k = kdmState(d);
  const icon = k.key === 'on_drive' ? <CheckIcon /> : k.key === 'missing' ? <WarnIcon /> : undefined;
  return (
    <Tooltip title={k.tooltip || ''} arrow>
      <Chip size="small" icon={icon} label={k.label} color={k.color}
            variant={k.key === 'on_drive' || k.key === 'missing' ? 'filled' : 'outlined'} />
    </Tooltip>
  );
}

function StatusChip({ d }) {
  const st = physicalStatus(d);
  return (
    <Tooltip title={st.tooltip || ''} arrow>
      <Chip size="small" label={st.label} color={st.color} />
    </Tooltip>
  );
}

function TrackingChip({ d, copyTracking }) {
  return (
    <Tooltip title={`Click to copy. ${d.carrier || 'Carrier'} ${d.tracking_number}` +
                    (d.shipped_at ? `, shipped ${new Date(d.shipped_at).toLocaleString()}` : '')}>
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        icon={<ShippingIcon />}
        label={`${d.carrier ? d.carrier + ' ' : ''}${d.tracking_number}`}
        sx={{ maxWidth: 240, cursor: 'pointer' }}
        onClick={() => copyTracking(d)}
      />
    </Tooltip>
  );
}

function DeliveryCard({
  d, busy, node, nodeStatus, driveBusyDelivery, driveMountedFor, assignDelivery, onEject,
  openShip, openFedex, openDelivered, printManifest, refreshTracking, copyTracking,
}) {
  const mounted = driveMountedFor(d);
  const step = nextStep(d, { driveMounted: mounted });
  const idx = stageIndex(d);
  const failed = d.status === 'failed';
  const hasContent = ['staged', 'shipped'].includes(d.status);
  const actionable = ['eject', 'ship'].includes(step.key);

  let hint = step.hint;
  if (step.key === 'assign' && !nodeStatus?.mounted) hint = 'Plug in a drive and mount it above, then start the copy.';
  if (step.key === 'eject' && mounted === null) {
    hint = `Can't confirm whether the drive on ${d.node_name || node?.name || 'the Pi'} is still mounted. Eject it there first.`;
  }

  return (
    <Paper variant="outlined" sx={{
      p: 1.5,
      borderColor: failed ? 'error.main' : actionable ? 'success.main' : 'divider',
      borderWidth: actionable || failed ? 2 : 1,
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={d.dcp_name || ''} placement="top-start">
            <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {deliveryTitle(d)}
            </Typography>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            #{d.id} · {d.venue_name || 'No venue'}
            {d.ship_to_name ? ` · ship to ${d.ship_to_name}` : ''}
          </Typography>
        </Box>
        <StatusChip d={d} />
      </Stack>

      {idx >= 0 && (
        <>
          <Stepper activeStep={idx} alternativeLabel
                   sx={{ mt: 1.5, mb: 1, display: { xs: 'none', md: 'flex' },
                         '& .MuiStepLabel-label': { fontSize: '0.72rem', mt: 0.5 } }}>
            {DRIVE_STAGES.map((s, i) => (
              <Step key={s.key} completed={i < idx}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
            Step {idx + 1} of {DRIVE_STAGES.length}: {DRIVE_STAGES[idx].label}
          </Typography>
        </>
      )}

      {d.status === 'staging' && (
        <LinearProgress variant="determinate" value={d.progress_pct || 0} sx={{ my: 1, height: 6, borderRadius: 3 }} />
      )}

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 1 }}>
        {hasContent && (
          <Tooltip title={d.dcp_name ? `${d.dcp_name} on the drive, verified against the source` : 'Verified against the source'} arrow>
            <Chip size="small" color="success" icon={<CheckIcon />}
                  label={`DCP ${d.size_bytes ? bytesHuman(d.size_bytes) : 'copied'}`} />
          </Tooltip>
        )}
        <KdmChip d={d} />
        {d.drive_label && (
          <Chip size="small" variant="outlined" label={`Drive ${d.drive_label}`} sx={{ fontFamily: 'monospace' }} />
        )}
        {d.tracking_number && <TrackingChip d={d} copyTracking={copyTracking} />}
      </Stack>

      {failed && d.error_message && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }}>{d.error_message}</Alert>
      )}
      {!failed && d.error_message && (
        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{d.error_message}</Alert>
      )}

      <Divider sx={{ my: 1.25 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        {step.key === 'assign' && (driveBusyDelivery ? (
          <Tooltip title="Copy this delivery onto the same drive as the one already in the slot. Use for a multi-venue route where one drive carries DCPs for several theaters.">
            <span>
              <Button size="small" variant="contained" color="info"
                      disabled={busy || !nodeStatus?.mounted}
                      onClick={() => assignDelivery(d.id, { stack: true })}>
                Add to drive
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button size="small" variant="contained"
                  disabled={busy || !nodeStatus?.mounted}
                  onClick={() => assignDelivery(d.id)}>
            Assign + Stage
          </Button>
        ))}
        {step.key === 'eject' && (
          <>
            <Button size="small" variant="contained" color="success" startIcon={<EjectIcon />}
                    disabled={busy || mounted !== true} onClick={onEject}>
              Eject drive
            </Button>
            {mounted === null && (
              <Button size="small" variant="text" onClick={() => openShip(d)}>
                Already unplugged? Mark shipped
              </Button>
            )}
          </>
        )}
        {step.key === 'ship' && (
          <>
            <Button size="small" variant="contained" color="success" startIcon={<ShippingIcon />}
                    onClick={() => openShip(d)}>
              Mark shipped
            </Button>
            {/* v10.17.34: Schedule FedEx creates the shipment via the
                FedEx Ship API and flips the row to SHIPPED. */}
            <Button size="small" variant="outlined" onClick={() => openFedex(d)}>
              Schedule FedEx
            </Button>
          </>
        )}
        {step.key === 'mark_delivered' && (
          <Button size="small" variant="contained" onClick={() => openDelivered(d)}>
            Mark delivered
          </Button>
        )}
        {hint && (
          <Typography variant="body2" color={failed ? 'error.main' : 'text.secondary'} sx={{ flex: 1 }}>
            {hint}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} sx={{ ml: { sm: 'auto' }, flexShrink: 0 }}>
          {d.status === 'shipped' && !d.tracking_number && (
            <Button size="small" variant="text" onClick={() => openShip(d)}>Add tracking</Button>
          )}
          {d.carrier === 'FedEx' && d.tracking_number && d.status === 'shipped' && (
            <Button size="small" variant="text" onClick={() => refreshTracking(d)}>Refresh tracking</Button>
          )}
          {d.shipping_label_url && (
            <Button size="small" variant="text" component="a"
                    href={d.shipping_label_url} target="_blank" rel="noopener">
              Print label
            </Button>
          )}
          {canPrintManifest(d) && (
            <Button size="small" variant="text" onClick={() => printManifest(d)}>Print manifest</Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function HistoryTable({ rows, openDelivered, printManifest, copyTracking }) {
  return (
    <TableContainer sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Film · Venue</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>KDM</TableCell>
            <TableCell>Shipping</TableCell>
            <TableCell>Venue confirmation</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((d) => (
            <TableRow key={d.id} hover>
              <TableCell><code>{d.id}</code></TableCell>
              <TableCell sx={{ minWidth: 180 }}>
                <Typography variant="body2" fontWeight={500}>{deliveryTitle(d)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {d.venue_name || 'No venue'}{d.drive_label ? ` · ${d.drive_label}` : ''}
                </Typography>
              </TableCell>
              <TableCell><StatusChip d={d} /></TableCell>
              <TableCell><KdmChip d={d} /></TableCell>
              <TableCell sx={{ minWidth: 200 }}>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  {d.tracking_number && <TrackingChip d={d} copyTracking={copyTracking} />}
                  {/* v10.17.32: delivered = carrier POD (signature +
                      delivered_at). Click the chip to edit it. */}
                  {d.delivered_at && (
                    <Tooltip title={
                      (d.signed_for_by_name ? `Signed by ${d.signed_for_by_name}, ` : '') +
                      new Date(d.delivered_at).toLocaleString() + ' (click to edit)'
                    }>
                      <Chip
                        size="small" color="info"
                        label={d.signed_for_by_name ? `Delivered · signed by ${d.signed_for_by_name}` : 'Delivered'}
                        sx={{ maxWidth: 260, cursor: 'pointer' }}
                        onClick={() => openDelivered(d)}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                {/* Received / Tested are marked from the DCP Distribution
                    view (v10.18.20 split); read-only here. */}
                <Stack direction="row" spacing={0.5}>
                  {d.received_at && (
                    <Tooltip title={`${d.received_by_name || ''} ${new Date(d.received_at).toLocaleString()}`.trim()}>
                      <Chip size="small" color="success" label="Received" />
                    </Tooltip>
                  )}
                  {d.tested_at && (
                    <Tooltip title={
                      `${d.tested_by_name || ''} ${new Date(d.tested_at).toLocaleString()}`.trim() +
                      (d.test_notes ? `\n${d.test_notes}` : '')
                    }>
                      <Chip size="small" color="success" label="Tested" />
                    </Tooltip>
                  )}
                  {!d.received_at && !d.tested_at && (
                    <Typography variant="caption" color="text.secondary">Not yet</Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">
                {canPrintManifest(d) && (
                  <Button size="small" variant="text" onClick={() => printManifest(d)}>Print manifest</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
